import { NavLink } from "react-router-dom";
import {
  HomeIcon,
  UserGroupIcon,
  SparklesIcon,
  HomeModernIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  TrophyIcon,
  ChartBarIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../../hooks/useAuth";
import { SESSION_DISPLAY_LABEL, useSeason } from "../../context/SeasonContext";

interface NavItem {
  label: string;
  to: string;
  icon: React.ElementType;
  adminOnly?: boolean;
}

const navItems: NavItem[] = [
  { label: "Dashboard", to: "/dashboard", icon: HomeIcon },
  { label: "Campers", to: "/campers", icon: UserGroupIcon },
  { label: "Activities", to: "/activities", icon: SparklesIcon },
  { label: "Cabins", to: "/cabins", icon: HomeModernIcon },
  { label: "Reports", to: "/reports", icon: ChartBarIcon },
  { label: "Admin", to: "/admin", icon: Cog6ToothIcon, adminOnly: true },
];

interface Props {
  onClose?: () => void;
}

export default function Sidebar({ onClose }: Props) {
  const { user, logout } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "director";
  const {
    availableYears,
    selectedYear,
    activeSession,
    availableSessions,
    setSelectedYear,
    setActiveSession,
  } = useSeason();

  return (
    <nav className="h-full flex flex-col bg-forest-900 text-white select-none">
      {/* Logo / Brand */}
      <div className="flex items-start justify-between px-5 py-4 border-b border-forest-700">
        <div className="flex flex-col gap-2 min-w-0">
          {/* Primary: ACTS branding */}
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-amber-500 flex items-center justify-center flex-shrink-0">
              <TrophyIcon className="w-5 h-5 text-forest-900" />
            </div>
            <div>
              <p className="font-display font-bold text-sm leading-tight">ACTS</p>
              <p className="text-2xs text-forest-300 uppercase tracking-widest">Achievement Credits Tracking System</p>
            </div>
          </div>
          {/* Sub-brand: Camp Highlands logo */}
          <img src="/ch-logo-white.png" alt="Camp Highlands" className="h-6 w-auto opacity-70 ml-0.5" />
        </div>
        <button
          onClick={onClose}
          className="p-1 rounded-lg hover:bg-forest-700 lg:hidden text-forest-300 flex-shrink-0 mt-0.5"
          aria-label="Close navigation"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Season / Session selectors */}
      <div className="px-4 py-3 border-b border-forest-700 space-y-2">
        <div className="flex items-center gap-2">
          <label className="text-2xs text-forest-400 uppercase tracking-widest w-14 flex-shrink-0">Season</label>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="flex-1 text-xs bg-forest-800 border border-forest-600 text-white rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-amber-500 appearance-none cursor-pointer"
          >
            {availableYears.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
            {availableYears.length === 0 && (
              <option value={selectedYear}>{selectedYear}</option>
            )}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-2xs text-forest-400 uppercase tracking-widest w-14 flex-shrink-0">Session</label>
          <select
            value={activeSession}
            onChange={(e) => setActiveSession(e.target.value as typeof activeSession)}
            disabled={availableSessions.length === 0}
            className="flex-1 text-xs bg-forest-800 border border-forest-600 text-white rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-amber-500 appearance-none cursor-pointer disabled:opacity-50"
          >
            {availableSessions.length === 0 ? (
              <option value={activeSession}>No sessions</option>
            ) : (
              availableSessions.map((s) => (
                <option key={s} value={s}>{SESSION_DISPLAY_LABEL[s]}</option>
              ))
            )}
          </select>
        </div>
      </div>

      {/* Navigation links */}
      <div className="flex-1 overflow-y-auto scrollbar-thin px-3 py-4 space-y-0.5">
        {navItems.map((item) => {
          if (item.adminOnly && !isAdmin) return null;
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-150
                ${
                  isActive
                    ? "bg-forest-700 text-white"
                    : "text-forest-200 hover:bg-forest-800 hover:text-white"
                }`
              }
              onClick={onClose}
            >
              <Icon className="w-5 h-5 flex-shrink-0" />
              {item.label}
            </NavLink>
          );
        })}
      </div>

      {/* User footer */}
      <div className="border-t border-forest-700 px-3 py-4">
        <div className="flex items-center gap-3 px-2 mb-3">
          <div className="w-8 h-8 rounded-full bg-amber-500 flex items-center justify-center text-forest-900 text-xs font-bold flex-shrink-0">
            {user?.firstName?.[0]}
            {user?.lastName?.[0]}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium truncate">
              {user?.firstName} {user?.lastName}
            </p>
            <p className="text-2xs text-forest-300 capitalize">{user?.role}</p>
          </div>
        </div>
        <button
          onClick={logout}
          className="flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm text-forest-300 hover:bg-forest-800 hover:text-white transition-colors"
        >
          <ArrowRightOnRectangleIcon className="w-4 h-4" />
          Sign out
        </button>
      </div>
    </nav>
  );
}
